import React from "react";
import { useLang } from "@/contexts/LanguageContext";
import { SITE } from "@/lib/i18n";
import { Instagram, Facebook, Music2, Phone, MessageCircle, Mail, MapPin } from "lucide-react";
import { MultidentLogo } from "@/components/Logo";

export const Footer = () => {
  const { t } = useLang();
  return (
    <footer
      data-testid="footer"
      className="bg-[#0a0a0a] text-white pt-20 pb-10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-5">
            <a href="#top" className="inline-block">
              <MultidentLogo invert />
            </a>
            <p className="mt-6 text-white/60 text-sm max-w-sm leading-relaxed">
              {t.footer.tagline}
            </p>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-4">
              {t.footer.contact}
            </p>
            <div className="space-y-2">
              <a href={`tel:${SITE.phone_display.replace(/\s/g, "")}`} className="flex items-center gap-2 text-sm text-white/80 hover:text-[#D4AF37] transition-colors">
                <Phone size={14} className="text-[#D4AF37]" />
                {SITE.phone_display}
              </a>
              <a
                href={`https://wa.me/${SITE.whatsapp_number}?text=${encodeURIComponent(SITE.whatsapp_text_es)}`}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-whatsapp-link"
                className="flex items-center gap-2 text-sm text-white/80 hover:text-[#25D366] transition-colors"
              >
                <MessageCircle size={14} className="text-[#25D366]" />
                {SITE.whatsapp_display} <span className="text-[10px] tracking-widest uppercase text-white/40 ml-1">WhatsApp</span>
              </a>
              <a href={`mailto:${SITE.email}`} className="flex items-center gap-2 text-sm text-white/80 hover:text-[#D4AF37] transition-colors break-all">
                <Mail size={14} className="text-[#D4AF37] shrink-0" />
                {SITE.email}
              </a>
              <p className="flex items-start gap-2 text-sm text-white/80 mt-2">
                <MapPin size={14} className="text-[#D4AF37] mt-1 shrink-0" />
                {SITE.address}
              </p>
            </div>
          </div>

          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-4">
              {t.footer.hours}
            </p>
            <p className="text-sm text-white/80">{t.footer.hours_v}</p>
          </div>

          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-4">
              {t.footer.follow}
            </p>
            <div className="flex gap-3">
              <a
                href={SITE.instagram}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-instagram"
                className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
              >
                <Instagram size={16} />
              </a>
              <a
                href={SITE.facebook}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-facebook"
                className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
              >
                <Facebook size={16} />
              </a>
              <a
                href={SITE.tiktok}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-tiktok"
                aria-label="TikTok"
                className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
              >
                <Music2 size={16} />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/40">
            © {new Date().getFullYear()} Multident. {t.footer.rights}
          </p>
          <p className="text-xs text-white/40 tracking-widest uppercase">
            Premium dental experience
          </p>
        </div>
      </div>
    </footer>
  );
};
